'use client'
import React, { useState } from 'react'
import type { RegulationsBlock as RegulationsBlockType } from '@/payload-types'
import RichText from '@/ui/components/RichText'
import { HeadingTag, Title } from '@/ui/components/Title'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/ui/atoms/Select'
import { Icon } from '@/ui/atoms/Icon'
import Accordion from '@/ui/atoms/Accordion'
import { FilterRow } from '@/ui/components/FilterRow'
import { sendGAEvent } from '@next/third-parties/google'
import { trackEvent } from '@/lib/analytics/posthog'
interface Props extends RegulationsBlockType {
  locale: string
  fixedTitleStyle?: HeadingTag
}

export const RegulationsBlock: React.FC<Props> = ({
  title,
  titleHierarchy,
  text,
  regulations,
  fixedTitleStyle,
}) => {
  const [selectedCountry, setSelectedCountry] = useState(
    regulations?.regulationsItems?.at(0)?.country,
  )

  const selectedRegulations = regulations?.regulationsItems?.filter(
    (item) => item.country === selectedCountry,
  )

  const handleValueChange = (country: string) => {
    // Log the country selection event to Google Analytics
    sendGAEvent('event', 'regulationsCountryChange', {country: country})
    trackEvent('regulationsCountryChange', { country })
    setSelectedCountry(country)
  }

  // Create array of unique country/icon pairs
  const uniqueCountries =
    regulations?.regulationsItems?.reduce((acc: Array<{ country: string; icon: string }>, item) => {
      if (!acc.some((x) => x.country === item.country)) {
        acc.push({
          country: item.country,
          icon: item.icon,
        })
      }
      return acc
    }, []) || []

  return (
    <section className="w-full @container/regulations">
      {title && (
        <Title hierarchy={titleHierarchy} fixedStyle={fixedTitleStyle}>
          {title}
        </Title>
      )}
      {text && <RichText data={text} enableGutter={false} className="lg:mt-8 mt-5" />}

      <div
        className={`w-full inline-flex justify-between items-start lg:mb-8 mb-5 mt-14 gap-3 flex-wrap`}
      >
        {regulations?.title && (
          <h3 className={'text-body-M-semibold text-text-base'}>{regulations?.title}</h3>
        )}

        {uniqueCountries.length >= 5 ? (
          <Select onValueChange={handleValueChange} value={selectedCountry}>
            <SelectTrigger className="w-auto">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {uniqueCountries.map(({ country, icon }, index) => {
                return (
                  <SelectItem key={index} value={country}>
                    <span className={'flex items-center flex-row gap-2 !text-text-subtle'}>
                      <Icon name={icon} /> {country}
                    </span>
                  </SelectItem>
                )
              })}
            </SelectContent>
          </Select>
        ) : (
          <FilterRow
            filters={uniqueCountries.map((item) => {
              return {
                value: item.country,
                label: (
                  <div className={'flex flex-row justify-center items-center gap-2'}>
                    <Icon name={item.icon} />
                    {selectedCountry === item.country ? item.country : null}
                  </div>
                ),
              }
            })}
            currentFilter={selectedCountry ?? ''}
            onFilterChange={(country) => setSelectedCountry(country)}
            includeAllFilter={false}
            analyticsContext={{ filter_category: 'regulations_country' }}
          />
          // } currentFilter={} onFilterChange={}
          // <div className="flex gap-2 items-center bg-bg-subtle-blue p-2 rounded-md">
          //   {uniqueCountries.map(({ country, icon }, index) => {
          //     const isActive = country === selectedCountry
          //     return (
          //       <Button
          //         key={index}
          //         size={'sm'}
          //         variant={isActive ? 'primary' : 'secondary'}
          //         onClick={() => handleValueChange(country)}
          //         aria-label={country}
          //       >
          //         <Icon name={icon} />
          //         {isActive && <span className="text-sm">{country}</span>}
          //       </Button>
          //     )
          //   })}
          // </div>
        )}
      </div>

      <div className={'flex flex-col gap-4'}>
        {selectedRegulations && (
          <Accordion
            openFirstByDefault={true}
            appearance={'outlined'}
            items={selectedRegulations.map((item) => ({
              title: item.title ?? '',
              content: item.text,
              button: item.button,
            }))}
            hasNumeration={false}
            analyticsContext={{ accordion_type: 'regulations' }}
          />
        )}
      </div>
    </section>
  )
}
